import { useRef, useState } from 'react';
import {
  care_api_error,
  new_care_draft
} from '../../../util_components/care_request/care_request_rules';
import {
  save_care_request,
  upload_care_screenshot
} from '../../../util_components/care_request/care_request_api';

// A changed file must be uploaded again; anything else keeps the uploaded key.
export const care_patch_keeps_screenshot = (patch) => !('file' in patch);

/*
 * Care's "who asked, and when" for one change modal. The screenshot is uploaded once, BEFORE the
 * booking change, so a failed upload changes nothing; the record is saved AFTER it.
 */
export default function useCareCapture({ session_uuid, kind, on_saved, on_retry_change }) {
  const [draft, setDraft] = useState(() => (kind ? new_care_draft(kind, Date.now()) : null));
  const [errors, setErrors] = useState({});
  const [retry, setRetryState] = useState(null); // {payload, on_done, busy, message}
  const screenshot_key = useRef('');

  const set_retry = (next) => {
    setRetryState(next);
    if (on_retry_change) on_retry_change(!!next);
  };

  const patch = (p) => {
    setDraft((d) => ({ ...d, ...p }));
    setErrors({});
    if (!care_patch_keeps_screenshot(p)) screenshot_key.current = '';
  };

  const upload_once = async () => {
    if (!draft || !draft.file || screenshot_key.current) return screenshot_key.current;
    const key = await upload_care_screenshot(session_uuid, draft.file);
    screenshot_key.current = key;
    return key;
  };

  const save_after_change = (payload, on_done) =>
    save_care_request(payload)
      .then(() => {
        set_retry(null);
        setDraft(null);
        on_saved();
        on_done();
      })
      .catch((e) =>
        set_retry({
          payload,
          on_done,
          busy: false,
          message: care_api_error(e, 'Try again.').message
        })
      );

  const retry_save = () => {
    set_retry({ ...retry, busy: true });
    save_after_change(retry.payload, retry.on_done);
  };

  return { draft, errors, setErrors, patch, upload_once, save_after_change, retry, retry_save };
}
